import { useCallback, useEffect, useRef, useState } from 'react';
import { subscriptionApi } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { isViewingAsSomeoneElse } from '@/lib/viewAs';

/****************
 * A display preference that follows the user, not the browser.
 *
 * Guillaume, 2026-09-03: "make it so when I go on the other version, my changes
 * remains." The watchlist lived in localStorage on the web and AsyncStorage on
 * the phone — two stores that never meet — so arranging it on one device did
 * nothing on the other.
 *
 * ── Local first, server behind it ───────────────────────────────────────────
 *
 * The local copy is still written, and it is what renders on the next load
 * before the server answers. Dragging a row must not wait on a round trip, and
 * a user on a bad connection should still get their own arrangement rather than
 * the defaults. The server copy is the one that TRAVELS; the local copy is the
 * one that is FAST. Neither is redundant.
 *
 * ── Writes are debounced, reads happen once ─────────────────────────────────
 *
 * A drag fires a change per row crossed. Sending each one would be a dozen
 * requests for one gesture and would race itself — the last response is not
 * necessarily the last state. One write, after the gesture settles.
 *
 * The server is read ONCE per signed-in user rather than polled. A preference is
 * not live data, and re-reading it while a user is arranging rows would let a
 * stale response overwrite what they are doing.
 *
 * ── The read waits for the user to exist ────────────────────────────────────
 *
 * `X-User-ID` is set from an auth effect, so a fetch on this hook's own mount
 * can go out before anyone is signed in — and the backend answers an
 * unidentified request as "dev-user" rather than rejecting it, so the response
 * is a perfectly successful `null`. Keyed on the user id, and skipped until
 * there is one. (Found on mobile 2026-09-04, where it meant the phone never
 * picked up the web's arrangement; the same race exists here.)
 ****************/

const WRITE_DEBOUNCE_MS = 800;

/* Every instance of one key in this app hears a change made by another (round 10 #400: the
   Traders page's group picker chose a group, but the page's own copy of the choice only
   caught up after a refresh). */
const peers = new Map<string, Set<(v: unknown) => void>>();
function tellPeers(key: string, from: (v: unknown) => void, v: unknown) {
  for (const l of peers.get(key) ?? []) if (l !== from) l(v);
}

export function useSyncedPreference<T>(key: string, fallback: T) {
  const { user } = useAuth();
  const userId = (user as any)?.id ?? null;
  /*
   * VIEWING SOMEONE ELSE: THEIR SERVER COPY OR NOTHING. 2026-09-21.
   *
   * The local copy is a cache of the signed-in browser's own person. Opened
   * from the admin dashboard with ?viewAs=, that person is the ADMIN -- so the
   * first paint showed the admin's value, and it stayed on screen whenever the
   * viewed user's server copy was empty. Guillaume opened a user's screen and
   * the Orb showed his own conversation. Nothing is read from or written to
   * this browser's storage in that mode; the viewed user's value comes from
   * the server, which answers for them (api/client sends their id).
   */
  const viewing = isViewingAsSomeoneElse();
  const readLocal = (): T => {
    if (viewing) return fallback;
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw) as T;
    } catch { /* a corrupt entry is not worth a crash */ }
    return fallback;
  };

  const [value, setValue] = useState<T>(readLocal);
  const [hydrated, setHydrated] = useState(false);
  // Set the moment the user changes anything, and never cleared. A server
  // response that arrives AFTER a local edit must not overwrite it — the user's
  // hand beats a request that was already in flight when they moved.
  const touched = useRef(false);
  /** Read by the updater form of `update`. A ref, not the state, so `update`
   *  keeps one identity across renders. */
  const valueRef = useRef<T>(value);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // Hear the same key changed by another instance (see `peers`).
  const hear = useRef<(v: unknown) => void>(() => {});
  hear.current = (v: unknown) => { touched.current = true; valueRef.current = v as T; setValue(v as T); };
  const listener = useRef((v: unknown) => hear.current(v)).current;
  useEffect(() => {
    let set = peers.get(key);
    if (!set) { set = new Set(); peers.set(key, set); }
    set.add(listener);
    return () => { set!.delete(listener); };
  }, [key, listener]);

  useEffect(() => {
    let alive = true;
    if (!userId) { setHydrated(true); return () => { alive = false; }; }
    subscriptionApi
      .getPreference(key)
      .then((res) => {
        if (!alive || touched.current) return;
        if (res && res.value !== null && res.value !== undefined) {
          valueRef.current = res.value as T;
          setValue(res.value as T);
          if (!viewing) {
            try { localStorage.setItem(key, JSON.stringify(res.value)); } catch { /* ignore */ }
          }
        }
      })
      .catch(() => {
        /* offline, or the endpoint is down: the local copy already rendered */
      })
      .finally(() => { if (alive) setHydrated(true); });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, userId]);

  /**
   * Accepts a value OR an updater, like `useState`'s setter.
   *
   * It used to take a value only -- and three of the four call sites in
   * LivePricesTable pass `prev => ...`, because a setter returned from a hook
   * looks exactly like a state setter. React's own `setState` resolves an
   * updater, so the SCREEN was right and nothing appeared wrong. Everything
   * else got the function:
   *
   *   JSON.stringify(fn)            -> undefined, so localStorage stored the
   *                                    literal string "undefined"
   *   putPreference(key, fn)        -> a body with no value, rejected by the
   *                                    server's json.Valid check
   *
   * and both failures were swallowed, one by a bare catch and one by
   * try/catch. Measured 2026-09-04: `copyfi.livePrices.rows` held exactly one
   * row across the whole database, 28 bytes, written by my own curl probe --
   * no real user had ever saved a watchlist. Guillaume, three times: "the Live
   * Price rows are still not sync web vs Mobile."
   *
   * Resolving the updater here means the call sites can keep the idiom they
   * already read as correct.
   */
  const update = useCallback(
    (next: T | ((prev: T) => T)) => {
      touched.current = true;
      const resolved = typeof next === 'function'
        ? (next as (prev: T) => T)(valueRef.current)
        : next;

      valueRef.current = resolved;
      setValue(resolved);
      tellPeers(key, listener, resolved);
      if (viewing) return; // read-only view: neither this browser nor their account
      try { localStorage.setItem(key, JSON.stringify(resolved)); } catch { /* ignore */ }

      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        // A failed write costs the sync, not the setting: the local copy stands
        // and the next change tries again. It says so, though -- silence here is
        // what let the bug above live.
        subscriptionApi.putPreference(key, resolved).catch((err) => {
          console.warn(`[preference] ${key} did not reach the server:`, err);
        });
      }, WRITE_DEBOUNCE_MS);
    },
    [key, viewing],
  );

  return [value, update, { hydrated }] as const;
}
